// Story 8.10 (ADO-2019), AC3 / AC4 / DV-22 / DV-32 / DV-35: the review of one waiting order
// (the 8.10 Figma exports, "HHT · Review · Waiting" and "Order 1 of 2"), before anything
// reaches a cart.
//
//   Ready to add      one row per depot cart the ready lines route to (DV-1, DV-30), with
//                     its lines and cases. The rows do not expand (OQ-21). With nothing
//                     ready the heading reads "0 lines · 0 cases" and no row shows (DV-55).
//   Needs attention   DISCONTINUED and UNAVAILABLE lines keep their pill: grey while a
//                     substitute will be added, red when none will (none offered, or
//                     removed), whatever the status. With a recommended substitute it is
//                     shown as "Substitute will be added" with "Remove substitute" (DV-4);
//                     removing it cannot be undone on the page (DV-49). Without a chosen
//                     substitute the card says it is not added; there is no "Search for
//                     replacement" button (DV-35). Unknown codes are never shown (DV-50).
//   Nothing addable   (Q6) no Ready line and no substitute kept, including once the last one
//                     is removed: a line Discontinued with none offered says so, a message
//                     says accepting closes the order, and the button reads "Accept and close
//                     order".
//   Maybe later / Add to carts   "Maybe later" leaves the order waiting (DV-9); "Add to
//                     carts", pressed after the substitutes are disclosed, is the
//                     acceptance AC4 asks for (DV-4). It is always enabled (except while
//                     the accept is in flight), even when nothing can be added: the accept
//                     still takes the order so it stops waiting (DV-23).
//
// Presentational: the page owns the read, the removed-substitute set, the accept and the
// navigation between waiting orders (DV-34).
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button } from '../../design-system';
import { casesLabel } from '../../basket/cartTotals';
import type { HhtAttentionLine, HhtOrderReview, HhtReviewDepot } from '../../api/hht-orders.client';
import { DepotMark } from './DepotMark';
import { HhtPill } from './HhtPill';
import { linesAndCases, linesLabel, substitutesLabel } from './hhtFormat';
import { CHOOSE_ROUTE } from '../../ordering/orderingChoices';
import linkButton from '../Cart/LinkButton.module.css';
import styles from './HhtOrders.module.css';

/** DV-35: an attention line that will not reach a cart (no substitute offered, or removed). */
export const NOT_ADDED_SEARCH_LATER =
  'Not added to carts. Search for a replacement after adding this order.';

/** Q6: a Discontinued line with no substitute offered, when nothing can be added. */
export const DISCONTINUED_NO_SUBSTITUTE =
  'Discontinued, with no substitute available. Not added to carts.';

/** Q6: the message above the actions when nothing can be added (Figma copy, its em-dash). */
export const NOTHING_CAN_BE_ADDED =
  'None of these lines can be added. Accepting closes this order — nothing will be added to your carts.';

/** Q6: whether accepting would add nothing: no Ready line and no substitute kept. */
export function hhtReviewAddsNothing(
  review: HhtOrderReview,
  removedSubstitutes: ReadonlySet<string>,
): boolean {
  return (
    review.ready.lineCount === 0 &&
    review.attention.every(
      (line) => chosenSubstitute(line, removedSubstitutes.has(line.skuCode)) === null,
    )
  );
}

export interface HhtReviewProps {
  readonly review: HhtOrderReview;
  /** The attention lines (by `skuCode`) whose substitute the Shopper removed. */
  readonly removedSubstitutes: ReadonlySet<string>;
  /** One-way until the review is reloaded (DV-49: no undo on the page). */
  readonly onRemoveSubstitute: (skuCode: string) => void;
  readonly onAccept: () => void;
  readonly accepting: boolean;
  /** An accept failure (an error Banner), rendered above the actions. */
  readonly acceptError?: ReactNode;
}

function DepotRow({ depot }: { readonly depot: HhtReviewDepot }) {
  return (
    <li className={`${styles.panel} ${styles.depotRow}`}>
      <DepotMark depotCode={depot.depotCode} />
      <span className={styles.depotName}>{depot.depotName}</span>
      <span className={styles.depotCounts}>{linesAndCases(depot.lineCount, depot.caseCount)}</span>
    </li>
  );
}

/** "{sku} · {depot} · {qty} cases", the depot left out when the line has none. */
function attentionMeta(line: HhtAttentionLine): string {
  return [line.skuCode, line.depotName, casesLabel(line.quantity)]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

/** The substitute the line will add: shown and not removed. */
function chosenSubstitute(line: HhtAttentionLine, removed: boolean) {
  return removed ? null : line.substitute;
}

function AttentionCard({
  line,
  removed,
  nothingAddable,
  onRemoveSubstitute,
}: {
  readonly line: HhtAttentionLine;
  readonly removed: boolean;
  readonly nothingAddable: boolean;
  readonly onRemoveSubstitute: (skuCode: string) => void;
}) {
  const name = line.productName ?? line.skuCode;
  const substitute = chosenSubstitute(line, removed);
  // Grey while a substitute will be added, red when none will, whatever the status.
  const tone = substitute !== null ? 'neutral' : 'error';
  const notAdded =
    nothingAddable && line.status === 'DISCONTINUED' && line.substitute === null
      ? DISCONTINUED_NO_SUBSTITUTE
      : NOT_ADDED_SEARCH_LATER;
  return (
    <li className={`${styles.panel} ${styles.attentionCard}`} aria-label={name}>
      <p className={styles.attentionName}>
        <span>{name}</span>
        <HhtPill tone={tone} dot>
          {line.status === 'DISCONTINUED' ? 'Discontinued' : 'Unavailable'}
        </HhtPill>
      </p>
      <p className={styles.attentionMeta}>{attentionMeta(line)}</p>
      {substitute !== null ? (
        <div className={styles.substitute}>
          <span className={styles.substituteText}>
            <span>
              Substitute will be added:{' '}
              <strong>{substitute.productName ?? substitute.skuCode}</strong>
            </span>
            <span>
              {[substitute.skuCode, casesLabel(substitute.quantity), substitute.depotName].join(
                ' · ',
              )}
            </span>
          </span>
          <Button variant="secondary" onClick={() => onRemoveSubstitute(line.skuCode)}>
            Remove substitute
          </Button>
        </div>
      ) : (
        <p className={styles.attentionDetail}>{notAdded}</p>
      )}
    </li>
  );
}

export function HhtReview({
  review,
  removedSubstitutes,
  onRemoveSubstitute,
  onAccept,
  accepting,
  acceptError,
}: HhtReviewProps) {
  const { ready, attention } = review;
  const substitutes = attention.filter(
    (line) => chosenSubstitute(line, removedSubstitutes.has(line.skuCode)) !== null,
  ).length;
  const nothingAddable = hhtReviewAddsNothing(review, removedSubstitutes);

  return (
    <>
      <div className={styles.head}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Review handheld order</h1>
          <HhtPill tone="warning">Waiting</HhtPill>
        </div>
        <p className={styles.subtitle}>
          Check counts and anything that needs attention, then add to your depot carts. Orders are
          not sent until you submit each cart.
        </p>
      </div>

      <section className={styles.section} aria-labelledby="hht-ready-title">
        <div className={styles.sectionHead}>
          <h2 id="hht-ready-title" className={styles.sectionTitle}>
            Ready to add
          </h2>
          <span className={styles.sectionSummary}>
            <strong>{linesAndCases(ready.lineCount, ready.caseCount)}</strong>
            {substitutes > 0 ? ` · ${substitutesLabel(substitutes)}` : null}
          </span>
        </div>
        {ready.depots.length > 0 ? (
          <ul className={styles.list}>
            {ready.depots.map((depot) => (
              <DepotRow key={`${depot.depotId}:${depot.entryKind ?? 'DEPOT'}`} depot={depot} />
            ))}
          </ul>
        ) : null}
      </section>

      {attention.length > 0 ? (
        <section className={styles.section} aria-labelledby="hht-attention-title">
          <div className={styles.sectionTitleRow}>
            <h2 id="hht-attention-title" className={styles.sectionTitle}>
              Needs attention
            </h2>
            <HhtPill tone="warning">{linesLabel(attention.length)}</HhtPill>
          </div>
          <ul className={styles.list}>
            {attention.map((line) => (
              <AttentionCard
                key={line.skuCode}
                line={line}
                removed={removedSubstitutes.has(line.skuCode)}
                nothingAddable={nothingAddable}
                onRemoveSubstitute={onRemoveSubstitute}
              />
            ))}
          </ul>
        </section>
      ) : null}

      {nothingAddable ? <p className={styles.nothingAddable}>{NOTHING_CAN_BE_ADDED}</p> : null}

      {acceptError}

      <div className={styles.actions}>
        <Link to={CHOOSE_ROUTE} className={`${linkButton.linkButton} ${linkButton.secondary}`}>
          Maybe later
        </Link>
        <Button variant="primary" onClick={onAccept} disabled={accepting} aria-busy={accepting}>
          {accepting
            ? nothingAddable
              ? 'Closing the order…'
              : 'Adding to carts…'
            : nothingAddable
              ? 'Accept and close order'
              : 'Add to carts'}
        </Button>
      </div>
    </>
  );
}
